// Interpreta o CRLV (e outros documentos do veículo) e extrai os dados do cadastro de veículo:
// placa, RENAVAM, chassi, motor, marca/modelo, cor e ano (fabricação/modelo). Funções puras, sem dependências.
//
// Duas fontes:
//  - itens de texto com posição (PDF digital, como o CRLV-e): cada rótulo fica logo acima do seu valor,
//    então lemos pela posição. Não precisa de OCR e não erra letra.
//  - texto corrido de OCR (foto ou PDF escaneado): melhor esforço, procurando pelos rótulos.

export const placaValida = (p) => /^[A-Z]{3}\d[A-Z0-9]\d{2}$/.test(p || '');

// VIN tem 17 caracteres e nunca usa as letras I, O e Q
export const chassiValido = (c) => /^[A-HJ-NPR-Z0-9]{17}$/.test(c || '');

// RENAVAM: 9 a 11 dígitos; o último é o dígito verificador (módulo 11, pesos 3298765432)
export function renavamValido(valor) {
  const r = (valor || '').replace(/\D/g, '');
  if (r.length < 9 || r.length > 11) return false;
  const completo = r.padStart(11, '0');
  const pesos = [3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const soma = pesos.reduce((acc, peso, i) => acc + Number(completo[i]) * peso, 0);
  const dv = (soma * 10) % 11;
  return (dv === 10 ? 0 : dv) === Number(completo[10]);
}

// Padrão de placa gravado no sistema: com hífen (AAA-0A00). A busca por placa no backend é por igualdade exata,
// então uma placa gravada sem hífen não seria encontrada depois pela tela de Clientes.
export const formatarPlaca = (p) => {
  const s = (p || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return s.length === 7 ? `${s.slice(0, 3)}-${s.slice(3)}` : s;
};

const soAlfanumerico = (t) => (t || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const anoValido = (a) => /^(19|20)\d{2}$/.test(a || '');

// ─── Leitura por posição (PDF digital) ────────────────────────────────────────

const ROTULOS = {
  placa: /^PLACA$/i,
  renavam: /^C[ÓO]DIGO\s+RENAVAM$/i,
  chassi: /^CHASSI$/i,
  marcaModelo: /^MARCA\s*\/\s*MODELO/i,
  cor: /^COR\s+PREDOMINANTE$/i,
  anoFabricacao: /^ANO\s+FABRICA[ÇC][ÃA]O$/i,
  anoModelo: /^ANO\s+MODELO$/i,
  motor: /^(N[ºO°.]*\s*(D[OE]\s+)?)?MOTOR$/i,
};

const TOLERANCIA_X = 6; // o valor começa na mesma coluna do rótulo
const DISTANCIA_MAXIMA_Y = 32; // e fica logo abaixo dele
const MESMA_LINHA = 2;
const LACUNA_MAXIMA = 6; // pedaços do mesmo valor estão quase colados; colunas vizinhas ficam bem mais longe

// Valor impresso logo abaixo do rótulo (fonte maior que a do rótulo). No PDF, y cresce para cima.
function valorAbaixoDoRotulo(itens, regexRotulo) {
  const rotulo = itens.find((i) => regexRotulo.test(i.str.trim()));
  if (!rotulo) return '';

  const primeiro = itens
    .filter((i) => i !== rotulo && i.str.trim()
      && i.h >= rotulo.h + 2
      && Math.abs(i.x - rotulo.x) <= TOLERANCIA_X
      && rotulo.y - i.y > 0 && rotulo.y - i.y <= DISTANCIA_MAXIMA_Y)
    .sort((a, b) => (rotulo.y - a.y) - (rotulo.y - b.y))[0];
  if (!primeiro) return '';

  // O valor pode vir em pedaços na mesma linha: junta os que estão colados
  const mesmaLinha = itens
    .filter((i) => i.str.trim() && Math.abs(i.y - primeiro.y) <= MESMA_LINHA && i.x >= primeiro.x)
    .sort((a, b) => a.x - b.x);
  let texto = '';
  let fim = null;
  for (const item of mesmaLinha) {
    if (fim !== null && item.x - fim > LACUNA_MAXIMA) break;
    texto += `${texto ? ' ' : ''}${item.str.trim()}`;
    fim = item.x + item.w;
  }
  return texto;
}

// Formato comum às duas fontes: valida, normaliza e separa o que parece errado
function montarResultado(bruto) {
  const dados = {};
  const duvidas = [];

  const placa = soAlfanumerico(bruto.placa);
  if (placaValida(placa)) dados.placa = placa; else if (placa) duvidas.push('placa');

  const renavam = (bruto.renavam || '').replace(/\D/g, '');
  if (renavam.length >= 9 && renavam.length <= 11) {
    dados.renavam = renavam;
    if (!renavamValido(renavam)) duvidas.push('renavam');
  }

  const chassi = soAlfanumerico(bruto.chassi);
  if (chassiValido(chassi)) dados.chassi = chassi; else if (chassi.length >= 10) { dados.chassi = chassi; duvidas.push('chassi'); }

  const fabricacao = (bruto.anoFabricacao || '').replace(/\D/g, '');
  const modelo = (bruto.anoModelo || '').replace(/\D/g, '');
  if (anoValido(fabricacao) && anoValido(modelo)) dados.ano = `${fabricacao}/${modelo}`;
  else if (anoValido(modelo) || anoValido(fabricacao)) dados.ano = modelo || fabricacao;

  const marcaModelo = (bruto.marcaModelo || '').replace(/\s+/g, ' ').trim().toUpperCase();
  if (marcaModelo.length >= 3) dados.marcaModelo = marcaModelo;

  const cor = (bruto.cor || '').replace(/\s+/g, ' ').trim().toUpperCase();
  if (cor.length >= 3 && /^[A-ZÀ-Ý ]+$/.test(cor)) dados.cor = cor;

  // Motor: sem padrão fixo (varia por fabricante); exige ao menos um número e não pode ser o chassi/placa lidos
  const motor = soAlfanumerico(bruto.motor);
  if (motor.length >= 5 && motor.length <= 25 && /\d/.test(motor) && motor !== dados.chassi && motor !== dados.placa) {
    dados.motor = motor;
  }

  return { dados, duvidas };
}

// itens: [{ str, x, y, w, h }] — texto do PDF com a posição de cada pedaço
export function extrairVeiculoDeItens(itens) {
  const lista = itens || [];
  return montarResultado({
    placa: valorAbaixoDoRotulo(lista, ROTULOS.placa),
    renavam: valorAbaixoDoRotulo(lista, ROTULOS.renavam),
    chassi: valorAbaixoDoRotulo(lista, ROTULOS.chassi),
    marcaModelo: valorAbaixoDoRotulo(lista, ROTULOS.marcaModelo),
    cor: valorAbaixoDoRotulo(lista, ROTULOS.cor),
    anoFabricacao: valorAbaixoDoRotulo(lista, ROTULOS.anoFabricacao),
    anoModelo: valorAbaixoDoRotulo(lista, ROTULOS.anoModelo),
    motor: valorAbaixoDoRotulo(lista, ROTULOS.motor),
  });
}

// ─── Leitura de texto corrido (OCR de foto ou PDF escaneado) ───────────────────

// Procura o valor no mesmo trecho do rótulo (depois dele) ou nas linhas seguintes
// `ignorar`: valores que na verdade são outro rótulo da mesma linha (ex.: COMBUSTÍVEL ao lado de COR PREDOMINANTE)
function depoisDoRotulo(linhas, regexRotulo, regexValor, linhasAFrente = 2, ignorar = null) {
  for (let i = 0; i < linhas.length; i++) {
    const achado = linhas[i].match(regexRotulo);
    if (!achado) continue;
    const resto = linhas[i].slice(achado.index + achado[0].length);
    for (const trecho of [resto, ...linhas.slice(i + 1, i + 1 + linhasAFrente)]) {
      const valor = trecho.match(regexValor);
      if (valor && !(ignorar && ignorar.test(valor[0]))) return valor[0];
    }
  }
  return '';
}

export function extrairVeiculoDeTexto(texto) {
  const original = texto || '';
  const linhas = original.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  // O chassi é a única sequência de 17 caracteres válidos do documento
  const chassi = (original.toUpperCase().match(/\b[A-HJ-NPR-Z0-9]{17}\b/g) || []).find((c) => /\d/.test(c) && /[A-Z]/.test(c)) || '';

  const anos = (linha) => (linha.match(/\b(19|20)\d{2}\b/g) || []);
  let anoFabricacao = '';
  let anoModelo = '';
  for (let i = 0; i < linhas.length && !anoFabricacao; i++) {
    if (/ANO\s+FABRICA[ÇC][ÃA]O/i.test(linhas[i])) {
      const encontrados = [...anos(linhas[i]), ...anos(linhas[i + 1] || '')];
      [anoFabricacao, anoModelo] = [encontrados[0] || '', encontrados[1] || ''];
    }
  }

  return montarResultado({
    placa: depoisDoRotulo(linhas, /\bPLACA\b(?!\s*ANTERIOR)/i, /\b[A-Z]{3}[-\s]?\d[A-Z0-9]\d{2}\b/i),
    renavam: depoisDoRotulo(linhas, /RENAVAM/i, /\b\d{9,11}\b/),
    chassi,
    marcaModelo: depoisDoRotulo(linhas, /MARCA\s*\/\s*MODELO(\s*\/\s*VERS[ÃA]O)?/i, /[A-Z0-9][A-Z0-9/. -]{2,}/i, 1),
    // só a primeira palavra: no OCR a coluna do combustível costuma vir colada na cor
    cor: depoisDoRotulo(linhas, /COR\s+PREDOMINANTE/i, /[A-ZÀ-Ý]{3,}/, 1, /^(COMBUST[ÍI]VEL|PLACA|CHASSI|MOTOR)$/),
    anoFabricacao,
    anoModelo,
    // "MOTOR" como rótulo isolado (não pega "AUTOMOTORES"); o valor tem ao menos um número.
    // Ignora só-números de 9 a 14 dígitos: no OCR costumam ser RENAVAM/CPF/CNPJ da linha vizinha.
    motor: depoisDoRotulo(linhas, /\b(N[ºO°.]*\s*(D[OE]\s+)?)?MOTOR\b/i, /\b(?=[A-Z0-9]*\d)[A-Z0-9]{5,25}\b/i, 1, /^\d{9,14}$/),
  });
}
